<script setup>
import { computed } from 'vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import BaseTable from 'dashboard/components-next/table/BaseTable.vue';
import BaseTableRow from 'dashboard/components-next/table/BaseTableRow.vue';
import BaseTableCell from 'dashboard/components-next/table/BaseTableCell.vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  teamList: {
    type: Array,
    default: () => [],
  },
  selectedTeams: {
    type: Array,
    default: () => [],
  },
  updateSelectedTeams: {
    type: Function,
    default: () => {},
  },
});

const { t } = useI18n();

const isTeamSelected = teamId => {
  return props.selectedTeams.some(id => String(id) === String(teamId));
};

const allTeamsSelected = computed(
  () =>
    props.teamList.length > 0 &&
    props.teamList.every(team => isTeamSelected(team.id))
);

const someTeamsSelected = computed(
  () => props.selectedTeams.length > 0 && !allTeamsSelected.value
);

const handleSelectTeam = teamId => {
  const shouldRemove = isTeamSelected(teamId);
  let result = [];
  if (shouldRemove) {
    result = props.selectedTeams.filter(item => String(item) !== String(teamId));
  } else {
    result = [...props.selectedTeams, Number(teamId)];
  }
  props.updateSelectedTeams(result);
};

const toggleSelectAll = () => {
  if (allTeamsSelected.value) {
    props.updateSelectedTeams([]);
  } else {
    const result = props.teamList.map(item => Number(item.id));
    props.updateSelectedTeams(result);
  }
};

const headers = computed(() => [
  '',
  t('CANNED_MGMT.TEAMS.TEAM_NAME'),
  t('CANNED_MGMT.TEAMS.DESCRIPTION'),
]);
</script>

<template>
  <div class="flex flex-col w-full gap-2">
    <div v-if="teamList.length === 0" class="py-4 text-center text-xs text-n-slate-11">
      {{ t('CANNED_MGMT.TEAMS.NO_TEAMS') }}
    </div>

    <div v-else class="max-h-72 overflow-y-auto border border-n-slate-6 dark:border-n-slate-6 rounded-lg">
      <BaseTable :headers="headers" :items="teamList">
        <template #header-0>
          <div class="flex items-center ltr:pl-4 rtl:pr-4">
            <Checkbox
              :model-value="allTeamsSelected"
              :indeterminate="someTeamsSelected"
              :title="t('CANNED_MGMT.TEAMS.SELECT_ALL')"
              @change="toggleSelectAll"
            />
          </div>
        </template>
        <template #header-1="{ header }">
          <span class="ltr:pl-2 rtl:pr-2">
            {{ header }}
          </span>
        </template>
        <template #header-2="{ header }">
          <span class="ltr:pl-2 rtl:pr-2">
            {{ header }}
          </span>
        </template>

        <template #row="{ items }">
          <BaseTableRow v-for="team in items" :key="team.id" :item="team">
            <template #default>
              <BaseTableCell class="w-12 ltr:!pl-4 rtl:!pr-4">
                <div class="flex items-center">
                  <Checkbox
                    :model-value="isTeamSelected(team.id)"
                    @change="() => handleSelectTeam(team.id)"
                  />
                </div>
              </BaseTableCell>

              <BaseTableCell class="min-w-0 max-w-44 ltr:!pl-2 rtl:!pr-2">
                <div class="flex items-center gap-2.5 min-w-0">
                  <div class="size-6 rounded-full bg-n-brand/10 text-n-brand flex items-center justify-center flex-shrink-0">
                    <span class="i-lucide-users size-3.5" />
                  </div>
                  <h4 class="text-sm font-medium mb-0 text-n-slate-12 truncate capitalize">
                    {{ team.name }}
                  </h4>
                </div>
              </BaseTableCell>

              <BaseTableCell class="min-w-0 ltr:!pl-2 rtl:!pr-2">
                <span class="text-xs text-n-slate-11 truncate block">
                  {{ team.description || '---' }}
                </span>
              </BaseTableCell>
            </template>
          </BaseTableRow>
        </template>
      </BaseTable>
    </div>

    <div class="flex items-center justify-between text-xs text-n-slate-11 px-1">
      <span>
        {{
          t('CANNED_MGMT.TEAMS.SELECTED_COUNT', {
            selected: selectedTeams.length,
            total: teamList.length,
          })
        }}
      </span>
      <span v-if="selectedTeams.length === 0" class="text-n-amber-11 font-medium">
        {{ t('CANNED_MGMT.TEAMS.ALL_TEAMS_HINT') }}
      </span>
    </div>
  </div>
</template>
